import { useMemo } from 'react';
import cn from 'classnames';
import { VStack } from '@direct-frontend/components';
import { getSettingLoyaltyProgramUrl } from '@/entities/merchSetting';
import { useRegisterLoyaltyAndReassignOrder } from '@/entities/order';
import { type LoyaltyRegistrationStep } from '@/shared/api/loyalty';
import { type OrderLoyalty } from '@/shared/api/order';
import { Drawer, type DrawerProps } from '@/shared/components/Drawer';
import { makeModifierClassname } from '@/shared/lib/makeModifierClassname';
import { getRequestContext, type RequestContext } from '@/shared/lib/requestContext';
import { LoyaltyRegistrationButton } from './components/LoyaltyRegistrationButton';
import { LoyaltyRegistrationError } from './components/LoyaltyRegistrationError';
import {
    LoyaltyRegistrationNoPhoneButton,
    LoyaltyRegistrationNoPhoneContent,
} from './components/LoyaltyRegistrationNoPhone';
import { LoyaltyRegistrationPromo } from './components/LoyaltyRegistrationPromo';
import styles from './LoyaltyRegistrationModal.module.css';

export interface LoyaltyRegistrationModalUIProps extends Pick<DrawerProps, 'isOpen' | 'onClose'> {
    userPhone: string;
    error: unknown;
    step: LoyaltyRegistrationStep;
    isLoading: boolean;
    phones: RequestContext['company']['phones'];
    emails: RequestContext['company']['emails'];
    potentialBonuses: number;
    welcomeBonuses: number;
    loyaltyProgramUrl: string;
    onRegister: VoidFunction;
}

const contentModifier = makeModifierClassname(styles, 'content');

export const LoyaltyRegistrationModalUI = (props: LoyaltyRegistrationModalUIProps) => {
    const {
        isOpen,
        onClose,
        step,
        userPhone,
        error,
        isLoading,
        phones,
        emails,
        loyaltyProgramUrl,
        welcomeBonuses,
        potentialBonuses,
        onRegister,
    } = props;

    const content = useMemo(() => {
        if (!userPhone) {
            return <LoyaltyRegistrationNoPhoneContent />;
        }

        if (error) {
            return <LoyaltyRegistrationError userPhone={userPhone} phones={phones} emails={emails} />;
        }

        return (
            <LoyaltyRegistrationPromo
                step={step}
                welcomeBonuses={welcomeBonuses}
                potentialBonuses={potentialBonuses}
                loyaltyProgramUrl={loyaltyProgramUrl}
            />
        );
    }, [userPhone, error, step, welcomeBonuses, potentialBonuses, loyaltyProgramUrl, phones, emails]);

    const button = useMemo(() => {
        if (!userPhone) {
            return <LoyaltyRegistrationNoPhoneButton />;
        }

        if (error) {
            return null;
        }

        return (
            <LoyaltyRegistrationButton
                onRegister={onRegister}
                isLoading={isLoading}
                loyaltyProgramUrl={loyaltyProgramUrl}
            />
        );
    }, [error, isLoading, loyaltyProgramUrl, onRegister, userPhone]);

    return (
        <Drawer isOpen={isOpen} onClose={onClose} snapPoints={[1]} size="auto">
            <VStack className={cn(contentModifier.className, contentModifier('withoutButton', !button))}>
                {content}
            </VStack>

            {button && <VStack className={styles.button}>{button}</VStack>}
        </Drawer>
    );
};

interface LoyaltyRegistrationModalProps extends Pick<DrawerProps, 'isOpen' | 'onClose'> {
    loyalty?: OrderLoyalty;
    userPhone: string;
    step: LoyaltyRegistrationStep;
}

export const LoyaltyRegistrationModal = (props: LoyaltyRegistrationModalProps) => {
    const { loyalty, userPhone, step, ...drawerProps } = props;

    const {
        company: { companyShopUrl, phones, emails },
    } = getRequestContext();

    const loyaltyProgramUrl = getSettingLoyaltyProgramUrl(companyShopUrl);

    const [register, { error, isLoading }] = useRegisterLoyaltyAndReassignOrder({ onSuccess: props.onClose });

    if (!loyalty || loyalty.is_registered_user) {
        return null;
    }

    return (
        <LoyaltyRegistrationModalUI
            {...drawerProps}
            userPhone={userPhone}
            error={error}
            step={step}
            isLoading={isLoading}
            phones={phones}
            emails={emails}
            loyaltyProgramUrl={loyaltyProgramUrl}
            welcomeBonuses={loyalty.unregistered_user_loyalty.welcome_bonus || 0}
            potentialBonuses={loyalty.unregistered_user_loyalty.potential_bonuses || 0}
            onRegister={register}
        />
    );
};
